import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const contactInfo = [{
  icon: MapPin,
  title: 'Visit Us',
  details: ['108 Peace Valley Road', 'Rishikesh, Uttarakhand', 'India - 249201'],
  link: 'https://maps.google.com'
}, {
  icon: Phone,
  title: 'Call Us',
  details: ['+91 98765 43210', '+91 98765 43211 (WhatsApp)'],
  link: 'tel:+919876543210'
}, {
  icon: Mail,
  title: 'Email Us',
  details: ['contact@supradhyan.org', 'info@supradhyan.org'],
  link: 'mailto:contact@supradhyan.org'
}, {
  icon: Clock,
  title: 'Office Hours',
  details: ['Monday - Friday: 9:00 AM - 6:00 PM', 'Saturday: 10:00 AM - 4:00 PM', 'Sunday: Closed'],
  link: null
}];
export function ContactPage() {
  return <div className="min-h-screen bg-[#FFFBF5]" data-id="element-343">
      <Header data-id="element-344" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#8B9D83]/10 via-[#FAF7F2] to-[#B8A4C9]/10" data-id="element-345">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-346">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center max-w-3xl mx-auto" data-id="element-347">
            <h1 className="font-serif text-5xl md:text-6xl text-[#2C3E50] mb-6 leading-tight" data-id="element-348">
              Get in Touch
            </h1>
            <p className="text-xl text-[#5D6D7E] leading-relaxed" data-id="element-349">
              We'd love to hear from you. Whether you have questions about our
              courses, want to visit our center, or need guidance on your
              spiritual journey, we're here to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-white" data-id="element-350">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-351">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20" data-id="element-352">
            {contactInfo.map((info, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-[#FAF7F2] p-6 rounded-xl text-center" data-id="element-353">
                <div className="w-14 h-14 rounded-full bg-[#8B9D83]/20 flex items-center justify-center mx-auto mb-4" data-id="element-354">
                  <info.icon className="w-7 h-7 text-[#8B9D83]" data-id="element-355" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3" data-id="element-356">
                  {info.title}
                </h3>
                <div className="space-y-1" data-id="element-357">
                  {info.details.map((detail, i) => <p key={i} className="text-[#5D6D7E] text-sm" data-id="element-358">
                      {detail}
                    </p>)}
                </div>
                {info.link && <a href={info.link} className="inline-block mt-4 text-[#8B9D83] font-semibold text-sm hover:text-[#7A8C72] transition-colors" data-id="element-359">
                    {info.icon === MapPin ? 'Get Directions' : 'Contact Now'} →
                  </a>}
              </motion.div>)}
          </div>

          {/* Contact Form & Map */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-360">
            {/* Contact Form */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-361">
              <h2 className="font-serif text-3xl text-[#2C3E50] mb-6" data-id="element-362">
                Send Us a Message
              </h2>
              <p className="text-[#5D6D7E] mb-8" data-id="element-363">
                Fill out the form below and we'll get back to you within 24
                hours.
              </p>

              <form className="space-y-6" data-id="element-364">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-365">
                  <div data-id="element-366">
                    <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-367">
                      First Name *
                    </label>
                    <input type="text" required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" placeholder="John" data-id="element-368" />
                  </div>
                  <div data-id="element-369">
                    <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-370">
                      Last Name *
                    </label>
                    <input type="text" required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" placeholder="Doe" data-id="element-371" />
                  </div>
                </div>

                <div data-id="element-372">
                  <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-373">
                    Email *
                  </label>
                  <input type="email" required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" placeholder="john@example.com" data-id="element-374" />
                </div>

                <div data-id="element-375">
                  <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-376">
                    Phone
                  </label>
                  <input type="tel" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" placeholder="+1 (555) 000-0000" data-id="element-377" />
                </div>

                <div data-id="element-378">
                  <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-379">
                    Subject *
                  </label>
                  <select required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" data-id="element-380">
                    <option value="" data-id="element-381">Select a subject</option>
                    <option data-id="element-382">Course Inquiry</option>
                    <option data-id="element-383">Visit Information</option>
                    <option data-id="element-384">Volunteer Opportunity</option>
                    <option data-id="element-385">Donation Question</option>
                    <option data-id="element-386">General Question</option>
                    <option data-id="element-387">Other</option>
                  </select>
                </div>

                <div data-id="element-388">
                  <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-389">
                    Message *
                  </label>
                  <textarea required rows={6} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors resize-none" placeholder="Tell us how we can help you..." data-id="element-390" />
                </div>

                <button type="submit" className="w-full bg-[#8B9D83] hover:bg-[#7A8C72] text-white py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2" data-id="element-391">
                  <Send className="w-5 h-5" data-id="element-392" />
                  Send Message
                </button>

                <p className="text-xs text-[#5D6D7E] text-center" data-id="element-393">
                  By submitting this form, you agree to our privacy policy and
                  terms of service.
                </p>
              </form>
            </motion.div>

            {/* Map & Additional Info */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="space-y-8" data-id="element-394">
              {/* Map */}
              <div data-id="element-395">
                <h2 className="font-serif text-3xl text-[#2C3E50] mb-6" data-id="element-396">
                  Find Us
                </h2>
                <div className="aspect-video rounded-2xl overflow-hidden shadow-lg bg-gray-200" data-id="element-397">
                  <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3444.2779654726147!2d78.31675931512!3d30.086863981881!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39093e67cf93f111%3A0xcc78e2e72e8e9c1d!2sRishikesh%2C%20Uttarakhand!5e0!3m2!1sen!2sin!4v1234567890" width="100%" height="100%" style={{
                  border: 0
                }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-398" />
                </div>
              </div>

              {/* Quick Info */}
              <div className="bg-[#FAF7F2] p-8 rounded-2xl" data-id="element-399">
                <h3 className="font-serif text-2xl text-[#2C3E50] mb-6" data-id="element-400">
                  Before You Visit
                </h3>
                <ul className="space-y-4 text-[#5D6D7E]" data-id="element-401">
                  <li className="flex items-start gap-3" data-id="element-402">
                    <div className="w-2 h-2 rounded-full bg-[#8B9D83] mt-2 flex-shrink-0" data-id="element-403" />
                    <span data-id="element-404">
                      Please call ahead to schedule a visit or tour of our
                      facilities
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-405">
                    <div className="w-2 h-2 rounded-full bg-[#8B9D83] mt-2 flex-shrink-0" data-id="element-406" />
                    <span data-id="element-407">
                      Modest, comfortable clothing is recommended for all
                      visitors
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-408">
                    <div className="w-2 h-2 rounded-full bg-[#8B9D83] mt-2 flex-shrink-0" data-id="element-409" />
                    <span data-id="element-410">
                      Our campus is a quiet, contemplative space—please maintain
                      silence in meditation areas
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-411">
                    <div className="w-2 h-2 rounded-full bg-[#8B9D83] mt-2 flex-shrink-0" data-id="element-412" />
                    <span data-id="element-413">
                      Free parking is available on-site for all visitors
                    </span>
                  </li>
                </ul>
              </div>

              {/* Emergency Contact */}
              <div className="bg-[#8B9D83] text-white p-6 rounded-2xl" data-id="element-414">
                <h3 className="font-serif text-xl font-bold mb-3" data-id="element-415">
                  Emergency Contact
                </h3>
                <p className="mb-2" data-id="element-416">For urgent matters outside office hours:</p>
                <p className="text-lg font-semibold" data-id="element-417">+91 98765 43299</p>
                <p className="text-sm text-white/80 mt-2" data-id="element-418">
                  Available 24/7 for retreat participants and emergencies
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Link */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-419">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-420">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-421">
            <h2 className="font-serif text-3xl text-[#2C3E50] mb-4" data-id="element-422">
              Have Questions?
            </h2>
            <p className="text-lg text-[#5D6D7E] mb-8" data-id="element-423">
              Check out our FAQ page for answers to common questions about
              courses, accommodations, and meditation practices.
            </p>
            <button className="bg-[#8B9D83] hover:bg-[#7A8C72] text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg" data-id="element-424">
              View FAQ
            </button>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-425" />
    </div>;
}